import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, ArrowRight, CheckCircle, ChevronLeft, Layout, TrendingUp, Zap, User, Phone } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AuditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuditModal: React.FC<AuditModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    goal: '',
    name: '',
    contact: ''
  });

  if (!isOpen) return null;

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < 4) {
      setDirection(1);
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1 && step < 4) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('audit_requests')
        .insert([{
          website_url: formData.url,
          primary_goal: formData.goal,
          name: formData.name,
          email: formData.contact
        }]);

      if (error) {
        console.error('Supabase error:', error);
        alert('Failed to submit. Please try again.');
      } else {
        handleNext(); // Move to success step
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0,
    }),
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = formData.url.length > 3;
  const isStep3Valid = formData.name.length > 1 && formData.contact.length > 3;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-neutral-900 border border-neutral-800 rounded-2xl shadow-2xl shadow-blue-900/10 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header & Progress */}
        {step < 4 && (
          <div className="relative pt-6 px-6 pb-2">
            <div className="absolute top-0 left-0 right-0 h-1 bg-neutral-800">
              <motion.div
                className="h-full bg-blue-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            <div className="flex justify-between items-center mb-4">
              {step > 1 ? (
                <button onClick={handleBack} className="text-neutral-400 hover:text-white transition-colors">
                  <ChevronLeft size={20} />
                </button>
              ) : (
                <div /> // Spacer
              )}
              <span className="text-xs font-medium text-neutral-500 tracking-widest uppercase">
                Step {step} of 3
              </span>
              <button onClick={onClose} className="text-neutral-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar">
          <AnimatePresence mode='wait' custom={direction}>
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold text-white">Let's start with the basics.</h2>
                <p className="text-neutral-400">What is the website URL you want us to audit?</p>

                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Globe className="text-neutral-500 group-focus-within:text-blue-500 transition-colors" size={20} />
                  </div>
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => updateField('url', e.target.value)}
                    placeholder="www.yourcompany.com"
                    className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-neutral-600"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleNext}
                  disabled={!isStep1Valid}
                  className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${isStep1Valid
                    ? 'bg-white text-black hover:bg-neutral-200'
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                    }`}
                >
                  Next Step <ArrowRight size={18} />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold text-white">What is your main goal?</h2>
                <p className="text-neutral-400">Select the primary reason for this audit.</p>

                <div className="space-y-3">
                  {[
                    { id: 'design', icon: Layout, label: 'Fix Design / Modernize', desc: 'My site looks outdated.' },
                    { id: 'growth', icon: TrendingUp, label: 'Get More International Leads', desc: 'I need more customers.' },
                    { id: 'tech', icon: Zap, label: 'Fix Technical Errors / Speed', desc: 'My site is too slow or broken.' }
                  ].map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        updateField('goal', option.id);
                        handleNext();
                      }}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-start gap-4 group ${formData.goal === option.id
                        ? 'bg-blue-500/10 border-blue-500/50'
                        : 'bg-neutral-950/50 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900'
                        }`}
                    >
                      <div className={`p-2 rounded-lg ${formData.goal === option.id ? 'bg-blue-500 text-white' : 'bg-neutral-800 text-neutral-400 group-hover:text-white'}`}>
                        <option.icon size={20} />
                      </div>
                      <div>
                        <h3 className={`font-medium ${formData.goal === option.id ? 'text-blue-400' : 'text-white'}`}>{option.label}</h3>
                        <p className="text-sm text-neutral-500 mt-0.5">{option.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-semibold text-white">Where should we send it?</h2>
                <p className="text-neutral-400">Ganesh will review your site and email the report.</p>

                <div className="space-y-4">
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="text-neutral-500 group-focus-within:text-blue-500 transition-colors" size={20} />
                    </div>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="Your Name"
                      className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-neutral-600"
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Phone className="text-neutral-500 group-focus-within:text-blue-500 transition-colors" size={20} />
                    </div>
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => updateField('contact', e.target.value)}
                      placeholder="Phone or Email"
                      className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-neutral-600"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!isStep3Valid}
                  className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${isStep3Valid
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:shadow-lg hover:shadow-blue-500/25 transform hover:scale-[1.02]'
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                    }`}
                >
                  Submit Request
                </button>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="text-center py-8 space-y-6"
              >
                <div className="w-20 h-20 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={40} className="animate-bounce" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2">Received!</h2>
                  <p className="text-neutral-400 max-w-xs mx-auto">
                    Ganesh will personally review your website and contact you within 24 hours with your custom plan.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="px-8 py-3 bg-neutral-800 text-white rounded-full font-medium hover:bg-neutral-700 transition-colors"
                >
                  Close
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};
